import React, { useEffect, useState } from 'react';
import { Button, Label, Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, Alert, Input, DropdownMenu, DropdownMenuTrigger, DropdownMenuLabel, DropdownMenuCheckboxItem, DropdownMenuSeparator, DropdownMenuContent, Tooltip, TooltipProvider, TooltipTrigger, TooltipContent, Switch, } from '@cargomatic/ui';
import { useTranslation } from 'react-i18next';
import { showToast } from 'stores/ToastStore';
import { overrideBillingStatus } from 'utils/ShipmentWebAPIUtils';
import { PENDING_REVIEW, READY_TO_BILL } from 'constants/billing-status';
import { Check, Info, PlusCircleIcon, TriangleAlert } from 'lucide-react';
import { capitalize, uniq } from 'lodash';
import { overrideBillingStatusReasonCodes } from 'constants/override-billing-status';
import SessionStore from 'stores/SessionStore';
export function BillingStatusPendingReviewModal({ shipmentIds = [], reasonCodes = [], note: incomingNote = '', open = false, onHide, onSuccess, showStatusToggle = false, }) {
    const { t } = useTranslation();
    const [readyToBill, setReadyToBill] = useState(false);
    const [selectedReasonCodes, setSelectedReasonCodes] = useState(reasonCodes || []);
    const [note, setNote] = useState(incomingNote);
    const [errors, setErrors] = useState({});
    const canChangeStatus = SessionStore.isBillingStatusAdmin() ||
        SessionStore.isBillingStatusManager();
    useEffect(() => {
        if (selectedReasonCodes.length > 0) {
            setReadyToBill(false);
        }
    }, [selectedReasonCodes]);
    function validate() {
        const errors = {};
        if (!note) {
            errors.note = `${t('Note')} ${t('REQUIRED')}`;
        }
        setErrors(errors);
        return errors;
    }
    function handleRemoveReasonCode(code) {
        return () => {
            setSelectedReasonCodes((items) => items.filter((item) => item !== code));
        };
    }
    function handleToggleReasonCode(code) {
        return (checked) => {
            if (checked) {
                setSelectedReasonCodes((prev) => uniq(prev.concat(code)));
            }
            else {
                setSelectedReasonCodes((prev) => prev.filter((current) => current !== code));
            }
        };
    }
    async function handleSave() {
        const errors = validate();
        if (Object.keys(errors).length > 0) {
            return;
        }
        try {
            const payload = shipmentIds.map((shipmentId) => ({
                _id: shipmentId,
                billing_status: readyToBill ? READY_TO_BILL : PENDING_REVIEW,
                pause_automated_billing_evaluation: readyToBill ? false : true,
                pause_automated_billing_evaluation_reason_code: note,
                pause_automated_billing_evaluation_reason_codes: selectedReasonCodes,
            }));
            await overrideBillingStatus(payload);
            showToast(`${t('BILLING_STATUS')} ${t('UPDATED')}`);
            if (onSuccess) {
                const results = payload.map((item) => ({
                    _id: item._id,
                    billing_status_state_billing_status: item.billing_status,
                    billing_status_state_pause_automated_billing_evaluation_reason_code: item.pause_automated_billing_evaluation_reason_code,
                    billing_status_state_pause_automated_billing_evaluation_reason_codes: item.pause_automated_billing_evaluation_reason_codes,
                }));
                onSuccess(results);
            }
        }
        catch (e) {
            showToast(`${t('BILLING_STATUS')} ${t('ERROR')}`);
            console.error(e);
        }
    }
    return (React.createElement(Dialog, { open: open, onOpenChange: onHide, className: "z-50" },
        React.createElement(DialogContent, { className: "sm:max-w-[600px]" },
            React.createElement(DialogHeader, null,
                React.createElement(DialogTitle, null, "Billing Status - Pending Review"),
                React.createElement(DialogDescription, null, "Move Billing Status to Pending Review")),
            shipmentIds.length > 1 && (React.createElement(Alert, { className: "" },
                React.createElement("div", { className: "flex" },
                    React.createElement(Info, { className: "w-4 h-4 mr-2" }),
                    React.createElement("span", null,
                        "Any changes here will bulk update the billing status (",
                        readyToBill ? 'Ready to Bill' : 'Pending Review',
                        "), the reason codes, and the note fields.")))),
            Object.values(errors).length > 0 && (React.createElement(Alert, { variant: "destructive", className: "" },
                React.createElement("div", { className: "flex" },
                    React.createElement(TriangleAlert, { className: "w-4 h-4 mr-2" }),
                    React.createElement("div", null,
                        "Please fill out all required fields (*)",
                        React.createElement("ul", { className: "my-2" }, Object.values(errors).map((error) => (React.createElement("li", { key: error }, error)))))))),
            React.createElement("div", { className: "space-y-4 py-4" },
                canChangeStatus && showStatusToggle && (React.createElement("div", { className: "flex items-center space-x-2 mb-4" },
                    React.createElement(Switch, { id: "pause-automated-evaluations", checked: readyToBill, onCheckedChange: setReadyToBill, disabled: selectedReasonCodes.length > 0 }),
                    React.createElement(Label, { htmlFor: "pause-automated-evaluations", className: "flex items-center space-x-1" },
                        React.createElement("span", null, capitalize(t('READY_TO_BILL'))),
                        React.createElement(TooltipProvider, null,
                            React.createElement(Tooltip, null,
                                React.createElement(TooltipTrigger, null,
                                    React.createElement(Info, { className: "w-4 h-4" })),
                                React.createElement(TooltipContent, { style: { zIndex: 3000 } }, t('PENDING_REVIEW_READY_TO_BILL_TOOLTIP_NOTE'))))))),
                React.createElement("div", { className: "" },
                    React.createElement(Label, { htmlFor: "reason-code", className: "block mb-1" },
                        t('REASON_CODE'),
                        " *"),
                    selectedReasonCodes.length > 0 && (React.createElement("ul", { className: "mb-4 space-y-1" }, selectedReasonCodes.map((item) => (React.createElement("li", { key: item, className: "flex justify-between items-center" },
                        React.createElement("span", null, t(item)),
                        React.createElement(TooltipProvider, null,
                            React.createElement(Tooltip, null,
                                React.createElement(TooltipTrigger, { asChild: true },
                                    React.createElement(Button, { variant: "icon", size: "icon", className: "hover:text-green-500", onClick: handleRemoveReasonCode(item) },
                                        React.createElement(Check, { className: "w-4 h-4" }))),
                                React.createElement(TooltipContent, null, "Click to mark completed")))))))),
                    React.createElement(DropdownMenu, { className: "mb-4" },
                        React.createElement(DropdownMenuTrigger, { asChild: true },
                            React.createElement(Button, { variant: "outline", size: "sm", className: "h-10 gap-1" },
                                React.createElement("span", { className: "sr-only sm:not-sr-only sm:whitespace-nowrap" }, "Select a reason code(s)"),
                                React.createElement(PlusCircleIcon, { className: "h-3.5 w-3.5" }))),
                        React.createElement(DropdownMenuContent, { align: "end" },
                            React.createElement(DropdownMenuLabel, null, " Select a reason code(s)"),
                            React.createElement(DropdownMenuSeparator, null),
                            overrideBillingStatusReasonCodes.map((condition) => (React.createElement(DropdownMenuCheckboxItem, { key: condition, onCheckedChange: handleToggleReasonCode(condition), checked: selectedReasonCodes.includes(condition) }, t(condition))))))),
                React.createElement("div", { className: "space-y-2" },
                    React.createElement(Label, { htmlFor: "note", className: "" },
                        t('Note'),
                        " *"),
                    React.createElement(Input, { value: note, id: "note", name: "note", onChange: (e) => setNote(e.target.value) }))),
            React.createElement(DialogFooter, null,
                React.createElement(Button, { type: "submit", onClick: handleSave }, t('SAVE'))))));
}
